<template>
  <div>
    <!-- Acciones -->
    <b-card no-body>
      <b-card-body>
        <b-row class="align-items-center">
          <b-col md="8">
            <b-button variant="success" @click="openUploader" class="mr-1">
              <feather-icon icon="UploadCloudIcon" class="mr-50" />
              Importar Excel (Stock)
            </b-button>

            <b-button
              variant="outline-info"
              :disabled="exporting"
              @click="exportSaldoFinal"
              class="mr-1"
            >
              <b-spinner small class="mr-50" v-if="exporting" />
              <feather-icon v-else icon="DownloadIcon" class="mr-50" />
              Exportar Saldo Final (3 hojas)
            </b-button>

            <b-input-group size="sm" class="d-inline-flex w-auto mr-2">
              <b-input-group-prepend is-text
                ><small>Excel:</small></b-input-group-prepend
              >
              <b-form-select
                v-model="selectedBatchId"
                :options="batchOptions"
                :disabled="loading || batches.length === 0"
              />
            </b-input-group>

            <b-button
              variant="danger"
              @click="onSoftDelete"
              :disabled="loading || !selectedBatchId"
            >
              <feather-icon icon="TrashIcon" class="mr-50" />
              Eliminar
            </b-button>
          </b-col>

          <b-col md="4" class="text-right">
            <b-input-group size="sm" class="d-inline-flex w-auto mr-1">
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="q"
                placeholder="Buscar modelo…"
                @input="applyFilter"
              />
            </b-input-group>
            <b-badge variant="light-primary"
              >Modelos: {{ totalElements }}</b-badge
            >
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Tabla principal (total por modelo) -->
    <b-card no-body>
      <b-table
        :items="pagedRecords"
        :fields="fields"
        :busy="loading"
        responsive
        small
        show-empty
        empty-text="Sin registros"
        class="position-relative"
      >
        <template #cell(stock_total)="ctx">
          <div class="text-right">
            {{ ctx.item.stock_total.toLocaleString() }}
          </div>
        </template>

        <template #cell(total_importado)="ctx">
          <div class="text-right">
            {{ ctx.item.total_importado.toLocaleString() }}
          </div>
        </template>

        <template #cell(resta)="ctx">
          <div
            class="text-right"
            :class="ctx.item.resta > 0 ? 'text-danger' : 'text-muted'"
          >
            {{ ctx.item.resta.toLocaleString() }}
          </div>
        </template>

        <template #cell(acciones)="ctx">
          <b-button
            size="sm"
            variant="outline-primary"
            class="mr-50"
            @click="openStores(ctx.item.modelo)"
          >
            Ver por tiendas
          </b-button>
          <b-button
            size="sm"
            variant="outline-success"
            :disabled="
              !ctx.item.restaPositiva || !duasByModelo[ctx.item.modeloMatch]
            "
            @click="openDuas(ctx.item)"
          >
            Ver DUAs
          </b-button>
        </template>

        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>Cargando…</strong>
          </div>
        </template>
      </b-table>

      <b-row class="mx-2 mb-2">
        <b-col sm="3" class="d-flex align-items-center">
          <span class="text-muted">Mostrar</span>
          <b-form-group class="mb-0 ml-50">
            <b-form-select
              v-model="perPage"
              :options="perPageOptions"
              @change="rebuildPagination"
            />
          </b-form-group>
          <span class="text-muted ml-50">entradas</span>
        </b-col>

        <b-col sm="3" class="mt-75" style="font-size: 12px">
          <span>{{ totalElements }} modelos</span>
        </b-col>

        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-sm-end"
        >
          <b-pagination
            v-model="currentPage"
            :total-rows="totalElements"
            :per-page="perPage"
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text
              ><feather-icon icon="ChevronLeftIcon" size="18"
            /></template>
            <template #next-text
              ><feather-icon icon="ChevronRightIcon" size="18"
            /></template>
          </b-pagination>
        </b-col>
      </b-row>
    </b-card>

    <!-- Modal: desglose por tienda -->
    <b-modal
      id="stores-modal"
      :title="`Stock por tiendas — Modelo ${selectedModelo || ''}`"
      hide-footer
    >
      <b-table
        :items="storesTable"
        :fields="[
          { key: 'tienda', label: 'Tienda' },
          {
            key: 'cantidad',
            label: 'Cantidad',
            thClass: 'text-right',
            tdClass: 'text-right',
          },
        ]"
        small
        show-empty
        empty-text="Sin tiendas"
      />
    </b-modal>

    <!-- Modal: DUAs y asignación de stock -->
    <b-modal
      id="duas-modal"
      :title="`DUAs — Modelo ${selectedModelo || ''}`"
      hide-footer
      size="lg"
    >
      <div class="mb-2">
        <b-badge variant="primary"
          >Modelo importación (match): {{ selectedModeloMatch || "—" }}</b-badge
        >
        <b-badge variant="primary" class="ml-50"
          >Stock total: {{ modalStockTotal.toLocaleString() }}</b-badge
        >
        <b-badge variant="primary" class="ml-50"
          >Total importado: {{ modalTotalImportado.toLocaleString() }}</b-badge
        >
      </div>
      <b-table
        :items="duasTable"
        :fields="[
          { key: 'dua', label: 'DUA' },
          {
            key: 'importador',
            label: 'Importador',
            thStyle: { width: '55%' },
            tdClass: 'importer-col',
          },
          {
            key: 'cantidad_dua',
            label: 'Cant. DUA',
            thClass: 'text-right',
            tdClass: 'text-right',
          },
          {
            key: 'peso_dua',
            label: '% del Modelo',
            thClass: 'text-right',
            tdClass: 'text-right',
          },
          {
            key: 'asignacion_stock',
            label: 'Asignación de Stock',
            thClass: 'text-right',
            tdClass: 'text-right',
          },
        ]"
        small
        show-empty
        empty-text="Sin DUAs"
      >
        <template #cell(peso_dua)="ctx"
          >{{ (ctx.item.peso_dua * 100).toFixed(2) }}%</template
        >
        <template #cell(asignacion_stock)="ctx">{{
          ctx.item.asignacion_stock.toLocaleString()
        }}</template>
      </b-table>
    </b-modal>

    <!-- Modal: subir Excel -->
    <b-modal id="upload-stock-modal" title="Importar Excel (Stock)" hide-footer>
      <b-form-file
        v-model="file"
        accept=".xls,.xlsx"
        @input="validateFile"
        browse-text="Elegir archivo"
      />
      <small class="text-muted d-block mt-1">
        Columnas esperadas: <b>cantidad</b>, <b>modelo</b> y (opcional)
        <b>tienda</b> (también acepta nombres similares).
      </small>
      <div class="text-right mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-50"
          @click="$bvModal.hide('upload-stock-modal')"
          >Cancelar</b-button
        >
        <b-button
          :disabled="!validFile || uploading"
          variant="primary"
          @click="submitUpload"
        >
          <b-spinner small v-if="uploading" class="mr-50" /> Subir
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */
import Vue from "vue";
import Ripple from "vue-ripple-directive";
import { BootstrapVue, BootstrapVueIcons, VBTooltip } from "bootstrap-vue";
import StockExcelService from "@/services/StockExcelService";
import ImportExcelService from "@/services/ImportExcelService";
import * as XLSX from "xlsx";

Vue.use(BootstrapVue);
Vue.use(BootstrapVueIcons);

export default {
  directives: { "b-tooltip": VBTooltip, Ripple },
  data() {
    return {
      fields: [
        { key: "modelo", label: "Modelo", sortable: true },
        {
          key: "stock_total",
          label: "Stock Total",
          thClass: "text-right",
          tdClass: "text-right",
        },
        {
          key: "total_importado",
          label: "Total Importación",
          thClass: "text-right",
          tdClass: "text-right",
        },
        {
          key: "resta",
          label: "Resta (Imp - Stock)",
          thClass: "text-right",
          tdClass: "text-right",
        },
        { key: "acciones", label: "Acciones" },
      ],

      rawRows: [],
      batches: [],
      selectedBatchId: null,
      importRows: [],
      exporting: false,
      importMap: {},
      duasByModelo: {},

      tableRows: [],
      filtered: [],

      loading: false,
      q: "",

      perPage: 10,
      currentPage: 1,
      perPageOptions: [10, 25, 50, 100],

      file: null,
      validFile: false,
      uploading: false,

      selectedModelo: null,
      storesTable: [],

      selectedModeloMatch: null,
      modalStockTotal: 0,
      modalTotalImportado: 0,
      duasTable: [],
    };
  },
  computed: {
    totalElements() {
      return this.filtered.length;
    },
    pagedRecords() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },
    batchOptions() {
      return this.batches.map((b) => ({
        value: b.id,
        text: `#${b.id} · ${b.filename || "sin nombre"} · ${new Date(
          b.uploadedAt
        ).toLocaleString()} · ${b._count?.items || 0} items`,
      }));
    },
  },
  mounted() {
    this.fetchAndBuild();
  },
  methods: {
    norm(s) {
      return (s || "")
        .toString()
        .toUpperCase()
        .replace(/\s+/g, "")
        .replace(/[-_]/g, "");
    },
    resolveImportModelFromStockModel(stockModelo, modelosImport) {
      const s = this.norm(stockModelo);
      let best = null;
      modelosImport.forEach((m) => {
        const nm = this.norm(m);
        if (!nm) return;
        if (s.includes(nm) || nm.includes(s)) {
          if (!best || nm.length > this.norm(best).length) best = m;
        }
      });
      return best;
    },

    async fetchBatches() {
      const resp = await StockExcelService.listBatches(this.$store);
      this.batches = !resp?.error && Array.isArray(resp.data) ? resp.data : [];

      const batchIds = Array.from(
        new Set((this.rawRows || []).map((r) => r.batchId).filter(Boolean))
      );
      if (batchIds.length === 1) this.selectedBatchId = batchIds[0];
      if (!this.selectedBatchId && this.batches.length)
        this.selectedBatchId = this.batches[0].id;
    },

    async onSoftDelete() {
      if (!this.selectedBatchId) {
        this.$toast && this.$toast.info("Selecciona un batch primero");
        return;
      }
      if (
        !confirm(
          `¿Eliminar lógicamente el batch #${this.selectedBatchId} y todos sus items?`
        )
      )
        return;

      this.loading = true;
      try {
        const resp = await StockExcelService.softDeleteBatch(
          this.selectedBatchId,
          this.$store
        );
        if (!resp?.error) {
          this.$toast && this.$toast.success("Batch eliminado");
          await Promise.all([this.fetchBatches(), this.fetchAndBuild()]);
          if (this.batches.every((b) => b.id !== this.selectedBatchId))
            this.selectedBatchId = null;
        } else {
          this.$toast &&
            this.$toast.error(resp.message || "No se pudo eliminar");
        }
      } catch (e) {
        this.$toast && this.$toast.error(e?.message || "Error al eliminar");
      } finally {
        this.loading = false;
      }
    },

    async fetchAndBuild() {
      try {
        this.loading = true;

        console.log(
          "StockExcelService?",
          !!StockExcelService,
          StockExcelService
        );
        console.log(
          "ImportExcelService?",
          !!ImportExcelService,
          ImportExcelService
        );
        if (
          !ImportExcelService ||
          typeof ImportExcelService.listItems !== "function"
        ) {
          throw new Error(
            "ImportExcelService no está disponible o no exporta listItems. Revisa el import/ruta."
          );
        }

        const [stockResp, importResp] = await Promise.all([
          StockExcelService.listItems(`?offset=0&limit=50000`, this.$store),
          ImportExcelService.listItems(`?offset=0&limit=50000`, this.$store),
        ]);

        const stockRows =
          (stockResp && (stockResp.data?.rows ?? stockResp.rows)) || [];
        const importRows =
          (importResp && (importResp.data?.rows ?? importResp.rows)) || [];

        this.rawRows = stockRows;
        this.importRows = importRows;

        this.importMap = {};
        this.duasByModelo = {};
        const totalByModelo = new Map();
        const sumByModeloDua = new Map();

        importRows.forEach((r) => {
          const m = r.modelo;
          totalByModelo.set(m, (totalByModelo.get(m) || 0) + (r.cantidad || 0));
          const key = `${m}|||${r.dua}`;
          sumByModeloDua.set(
            key,
            (sumByModeloDua.get(key) || 0) + (r.cantidad || 0)
          );
        });

        totalByModelo.forEach((total, modelo) => {
          this.importMap[modelo] = total;
          const list = [];
          sumByModeloDua.forEach((cant, key) => {
            const [m, dua] = key.split("|||");
            if (m !== modelo) return;
            const peso = total > 0 ? cant / total : 0;
            const imp =
              importRows.find((x) => x.modelo === m && x.dua === dua)
                ?.importador || null;
            list.push({
              dua,
              importador: imp,
              cantidad_dua: cant,
              peso_dua: peso,
            });
          });
          list.sort((a, b) => b.peso_dua - a.peso_dua);
          this.duasByModelo[modelo] = list;
        });

        const totStock = new Map();
        stockRows.forEach((r) => {
          totStock.set(
            r.modelo,
            (totStock.get(r.modelo) || 0) + (r.cantidad || 0)
          );
        });

        const modelosImport = Array.from(
          new Set(importRows.map((r) => r.modelo))
        ).filter(Boolean);
        const norm = (s) =>
          (s || "")
            .toString()
            .toUpperCase()
            .replace(/\s+/g, "")
            .replace(/[-_]/g, "");
        const resolveImportModelFromStockModel = (stockModelo) => {
          const s = norm(stockModelo);
          let best = null;
          modelosImport.forEach((m) => {
            const nm = norm(m);
            if (!nm) return;
            if (s.includes(nm) || nm.includes(s)) {
              if (!best || nm.length > norm(best).length) best = m;
            }
          });
          return best;
        };

        this.tableRows = Array.from(totStock.entries())
          .map(([modeloStock, stock_total]) => {
            const modeloMatch = resolveImportModelFromStockModel(modeloStock);
            const total_importado = modeloMatch
              ? this.importMap[modeloMatch] || 0
              : 0;
            const resta = total_importado - stock_total;
            return {
              modelo: modeloStock,
              modeloMatch,
              stock_total,
              total_importado,
              resta,
              restaPositiva: resta > 0,
            };
          })
          .sort((a, b) => a.modelo.localeCompare(b.modelo));

        this.applyFilter();
        this.currentPage = 1;
      } catch (e) {
        console.error(e);
        this.$toast &&
          this.$toast.error(e?.message || "Error al cargar stock/importación");
      } finally {
        this.fetchBatches();
        this.loading = false;
      }
    },

    applyFilter() {
      const q = (this.q || "").toLowerCase().trim();
      if (!q) this.filtered = this.tableRows.slice();
      else
        this.filtered = this.tableRows.filter((r) =>
          (r.modelo || "").toLowerCase().includes(q)
        );
      this.currentPage = 1;
    },
    rebuildPagination() {
      this.currentPage = 1;
    },

    openStores(modelo) {
      this.selectedModelo = modelo;
      const rows = this.rawRows.filter((r) => r.modelo === modelo);
      const byStore = new Map();
      rows.forEach((r) => {
        const t = r.tienda || "—";
        byStore.set(t, (byStore.get(t) || 0) + (r.cantidad || 0));
      });
      this.storesTable = Array.from(byStore.entries()).map(
        ([tienda, cantidad]) => ({ tienda, cantidad })
      );
      this.storesTable.sort((a, b) => a.tienda.localeCompare(b.tienda));
      this.$bvModal.show("stores-modal");
    },

    async exportSaldoFinal() {
      try {
        this.exporting = true;

        const resumen = this.tableRows.map((r) => ({
          modelo: r.modelo,
          stock_total: Number(r.stock_total || 0),
          total_importado: Number(r.total_importado || 0),
          resta: Number(r.resta || 0),
        }));

        const byStore = new Map();
        this.rawRows.forEach((r) => {
          const k = `${r.modelo}|||${r.tienda || "—"}`;
          byStore.set(k, (byStore.get(k) || 0) + (r.cantidad || 0));
        });
        const tiendas = Array.from(byStore.entries())
          .map(([k, cantidad]) => {
            const [modelo, tienda] = k.split("|||");
            return { modelo, tienda, cantidad: Number(cantidad || 0) };
          })
          .sort(
            (a, b) =>
              a.modelo.localeCompare(b.modelo) ||
              (a.tienda || "").localeCompare(b.tienda || "")
          );

        const duas = [];
        this.tableRows.forEach((row) => {
          const modeloStock = row.modelo;
          const modeloMatch = row.modeloMatch;
          const stockTotal = Number(row.stock_total || 0);
          if (!modeloMatch) return;

          const pack = this.duasByModelo[modeloMatch];
          if (!Array.isArray(pack) || !pack.length) return;

          for (const d of pack) {
            const pesoDecimal = Number(d.peso_dua || 0);
            const asignacion = Math.round(stockTotal * pesoDecimal);

            duas.push({
              modelo: modeloStock,
              dua: String(d.dua || "").trim(),
              importador: d.importador || null,
              cantidad_dua: Number(d.cantidad_dua || 0),
              porcentaje_modelo: pesoDecimal,
              asignacion_stock: asignacion,
            });
          }
        });

        duas.sort(
          (a, b) =>
            a.modelo.localeCompare(b.modelo) ||
            b.porcentaje_modelo - a.porcentaje_modelo ||
            a.dua.localeCompare(b.dua)
        );

        const wb = XLSX.utils.book_new();

        // Resumen
        const wsResumen = XLSX.utils.json_to_sheet(resumen);
        XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen");

        // Tiendas
        const wsTiendas = XLSX.utils.json_to_sheet(tiendas);
        XLSX.utils.book_append_sheet(wb, wsTiendas, "Tiendas");

        // DUAs
        const headerKeys = [
          "modelo",
          "dua",
          "importador",
          "cantidad_dua",
          "porcentaje_modelo",
          "asignacion_stock",
        ];
        const headerLabels = [
          "Modelo",
          "DUA",
          "Importador",
          "Cant. DUA",
          "% del Modelo",
          "Asignación de Stock",
        ];

        const wsDuas = XLSX.utils.json_to_sheet(duas, { header: headerKeys });
        XLSX.utils.sheet_add_aoa(wsDuas, [headerLabels], { origin: "A1" });

        const range = XLSX.utils.decode_range(wsDuas["!ref"] || "A1");
        for (let R = 1; R <= range.e.r; R++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: 4 });
          if (wsDuas[addr]) wsDuas[addr].z = "0.00%";
        }
        wsDuas["!cols"] = [
          { wch: 18 }, // Modelo
          { wch: 22 }, // DUA
          { wch: 120 }, // Importador
          { wch: 12 }, // Cant. DUA
          { wch: 14 }, // % del Modelo
          { wch: 16 }, // Asignación
        ];
        XLSX.utils.book_append_sheet(wb, wsDuas, "DUAs");

        XLSX.writeFile(wb, "saldo-final.xlsx");
      } catch (e) {
        console.error(e);
        this.$toast && this.$toast.error("No se pudo exportar el Excel");
      } finally {
        this.exporting = false;
      }
    },

    openDuas(row) {
      this.selectedModelo = row.modelo;
      this.selectedModeloMatch = row.modeloMatch || "—";
      this.modalStockTotal = row.stock_total || 0;
      this.modalTotalImportado = row.total_importado || 0;

      const duas =
        row.modeloMatch && this.duasByModelo[row.modeloMatch]
          ? this.duasByModelo[row.modeloMatch]
          : [];
      this.duasTable = duas.map((d) => ({
        ...d,
        asignacion_stock: Math.round(this.modalStockTotal * d.peso_dua),
      }));
      this.$bvModal.show("duas-modal");
    },

    openUploader() {
      this.file = null;
      this.validFile = false;
      this.uploading = false;
      this.$bvModal.show("upload-stock-modal");
    },
    validateFile(file) {
      this.validFile = !!file && /\.(xls|xlsx)$/i.test(file.name);
    },
    async submitUpload() {
      try {
        this.uploading = true;
        await StockExcelService.uploadExcel(this.file, this.$store);
        this.$bvModal.hide("upload-stock-modal");
        await this.fetchAndBuild();
        this.$toast && this.$toast.success("Stock importado correctamente");
      } catch (e) {
        this.$toast &&
          this.$toast.error(e?.message || "Error al importar stock");
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>
